import { defineStore } from 'pinia';
import type {
  AggregatedBundlePriceDto,
  AggregatedDiscountDto,
  AggregatedPlanDto,
} from '~/models/ListingProduct.api.model';
import {
  mapProductDataBundlePricesToPlanObject,
  type Plan,
} from '~/models/Plan.model';
import {
  AddBundleRequestType,
  type AddBundleToCartRequestModel,
} from '~/models/shopping_cart/AddBundleToCartRequest.api.model';

export type PlanConnection = {
  PlanId: number;
  FamilyPlanId: number;
};
export type ExistingCustomerPlan = {
  AllowedFamilyConnections: number;
  AllowedFamilyPlanId: number | null;
  DataAllowance: number;
  Number: string;
  PlanId: number;
  SocialSecurityNumber: string;
};

type AllowedFamilyPlanBindingPeriodMonths = 0 | 12 | 24;
const DEFAULT_MAX_ALLOWED_FAMILY_CONNECTIONS = 2;

const bindingPeriodOrDefault = (
  period: number,
): AllowedFamilyPlanBindingPeriodMonths => {
  return period === 0 || period === 12 || period === 24 ? period : 0;
};

export type ParentPlanIdToFamilyPlanIdMap = {
  [parentPlanIdAndAllowance: string]: number;
};

export const findBundlePriceByBindingPeriod =
  (bindingPeriodMonths: number) => (bp: AggregatedBundlePriceDto) =>
    bp.BindingPeriod.Months === bindingPeriodMonths;

type State = {
  parentPlanIdToFamilyPlanIdMap: ParentPlanIdToFamilyPlanIdMap;
  numberOfFamilyPlanConnections: number;
  selectedNewCustomerPlanId: string;
  selectedExistingCustomerPlanNumber: string;
  totalCost: number;
  allowedNumberOfConnections: number;
  inputs: {
    bindingPeriodRadioValue: string;
  };
  ui: {
    isProductPage: boolean;
    showNewCustomerFlow: boolean;
    showFamilyDrawer: boolean;
    showPdpDrawerStart: boolean;
    bankIdTestPassthroughIsSubmitting: boolean;
    bankIdTestPassthroughIsSelected: boolean;
    generalErrorMessage?: string;
    showFamilyAddedPdp: boolean;
    isBuyButtonLoading: boolean;
  };
  existingCustomer: {
    isIdentified: boolean;
    hasPlans: boolean;
    plans: ExistingCustomerPlan[];
  };
  FamilyPlansDiscounts: AggregatedDiscountDto[];
  ParentPlansDiscounts: AggregatedDiscountDto[];
  parentPlanBindingPeriodDiscounts: AggregatedDiscountDto[];
  parentPlanNonBindingPeriodDiscounts: AggregatedDiscountDto[];
};

export const useFamilyStore = defineStore('family', {
  state: (): State => ({
    parentPlanIdToFamilyPlanIdMap: {},
    numberOfFamilyPlanConnections: 0,
    selectedNewCustomerPlanId: '',
    selectedExistingCustomerPlanNumber: '',
    totalCost: 0,
    allowedNumberOfConnections: DEFAULT_MAX_ALLOWED_FAMILY_CONNECTIONS,
    inputs: {
      bindingPeriodRadioValue: '12',
    },
    ui: {
      isProductPage: false,
      showNewCustomerFlow: false,
      showFamilyDrawer: false,
      showPdpDrawerStart: false,
      bankIdTestPassthroughIsSubmitting: false,
      bankIdTestPassthroughIsSelected: false,
      showFamilyAddedPdp: false,
      isBuyButtonLoading: false,
    },
    existingCustomer: {
      isIdentified: false,
      hasPlans: false,
      plans: [],
    },
    FamilyPlansDiscounts: [],
    ParentPlansDiscounts: [],
    parentPlanBindingPeriodDiscounts: [],
    parentPlanNonBindingPeriodDiscounts: [],
  }),
  actions: {
    _initGeneral(): void {
      const planStore = usePlanStore();

      if (planStore.familyParentPlans.length > 0) {
        const familyParentPlans = planStore.familyParentPlans.flatMap(
          (plan) => plan.bundlePrices,
        );

        const defaultPlanId = `${planStore.familyParentPlans[0].planId}`;

        this.selectedNewCustomerPlanId =
          this._findPlanIdOfRegularPlanWithHighestAllowance(
            familyParentPlans,
            defaultPlanId,
          );
      }

      this._createFamilyPlanToParentPlanMap(planStore.plans);

      this.FamilyPlansDiscounts = this._getUniqueDiscounts(
        planStore.familyPlans,
      );

      this.ParentPlansDiscounts = this._getUniqueDiscounts(
        planStore.familyParentPlans,
      );

      this.parentPlanBindingPeriodDiscounts =
        this._getUniqueBindingPeriodDiscounts(planStore.familyParentPlans);

      this.parentPlanNonBindingPeriodDiscounts =
        this._getUniqueNonBindingPeriodDiscounts(planStore.familyParentPlans);
    },
    _initPDP(): void {
      const productPageStore = useMyProductPageStore();

      const currentVariantPlans = (
        productPageStore.currentVariant?.Prices.BundlePrices || []
      )
        .filter(
          (bp) =>
            !!bp.Plan && bp.BindingPeriod.Months === this.selectedBindingPeriod,
        )
        .map((bp) => bp.Plan)
        .filter((plan) => !!plan);

      const currentVariantFamilyPlanBundlePrices = (
        productPageStore.currentVariant?.Prices.BundlePrices || []
      ).filter((bp) => bp.Plan?.IsFamilyPlan === true);

      const currentVariantFamilyParentPlanBundlePrices = (
        productPageStore.currentVariant?.Prices.BundlePrices || []
      ).filter((bp) => bp.Plan?.IsFamilyAllowed === true);

      if (productPageStore.currentFamilyParentPlansForDrawer.length > 0) {
        const defaultPlanId = productPageStore
          .currentFamilyParentPlansForDrawer[0].Plan?.PlanId
          ? `${productPageStore.currentFamilyParentPlansForDrawer[0].Plan?.PlanId}`
          : '';

        this.selectedNewCustomerPlanId =
          this._findPlanIdOfRegularPlanWithHighestAllowance(
            currentVariantFamilyParentPlanBundlePrices,
            defaultPlanId,
          );
      }

      this._createFamilyPlanToParentPlanMapForPDP(currentVariantPlans);

      this.FamilyPlansDiscounts = this._getUniqueDiscountsForPDP(
        currentVariantFamilyPlanBundlePrices,
      );
      this.ParentPlansDiscounts = this._getUniqueDiscountsForPDP(
        currentVariantFamilyParentPlanBundlePrices,
      );
    },
    _getUniqueDiscounts(plans: Plan[]): AggregatedDiscountDto[] {
      const allDiscounts = plans
        .flatMap((plan) => plan.bundlePrices)
        .flatMap((bundlePrice) => bundlePrice.Discounts);

      const uniqueDiscountsMap = new Map<number, AggregatedDiscountDto>();
      allDiscounts.forEach((discount) => {
        if (!uniqueDiscountsMap.has(discount.DiscountId)) {
          uniqueDiscountsMap.set(discount.DiscountId, discount);
        }
      });

      return Array.from(uniqueDiscountsMap.values());
    },
    _getUniqueBindingPeriodDiscounts(plans: Plan[]): AggregatedDiscountDto[] {
      const allDiscounts = plans
        .flatMap((plan) => plan.bundlePrices)
        .filter((bundlePrice) => bundlePrice.BindingPeriod.Months > 0)
        .flatMap((bundlePrice) => bundlePrice.Discounts);

      const uniqueDiscountsMap = new Map<number, AggregatedDiscountDto>();
      allDiscounts.forEach((discount) => {
        if (!uniqueDiscountsMap.has(discount.DiscountId)) {
          uniqueDiscountsMap.set(discount.DiscountId, discount);
        }
      });

      return Array.from(uniqueDiscountsMap.values());
    },
    _getUniqueNonBindingPeriodDiscounts(
      plans: Plan[],
    ): AggregatedDiscountDto[] {
      const allDiscounts = plans
        .flatMap((plan) => plan.bundlePrices)
        .filter((bundlePrice) => bundlePrice.BindingPeriod.Months === 0)
        .flatMap((bundlePrice) => bundlePrice.Discounts);

      const uniqueDiscountsMap = new Map<number, AggregatedDiscountDto>();
      allDiscounts.forEach((discount) => {
        if (!uniqueDiscountsMap.has(discount.DiscountId)) {
          uniqueDiscountsMap.set(discount.DiscountId, discount);
        }
      });

      return Array.from(uniqueDiscountsMap.values());
    },
    _getUniqueDiscountsForPDP(
      bundlePrices: AggregatedBundlePriceDto[],
    ): AggregatedDiscountDto[] {
      const allDiscounts = bundlePrices.flatMap(
        (bundlePrice) => bundlePrice.Discounts,
      );

      const uniqueDiscountsMap = new Map<number, AggregatedDiscountDto>();
      allDiscounts.forEach((discount) => {
        if (!uniqueDiscountsMap.has(discount.DiscountId)) {
          uniqueDiscountsMap.set(discount.DiscountId, discount);
        }
      });

      return Array.from(uniqueDiscountsMap.values());
    },
    _findPlanIdOfRegularPlanWithHighestAllowance(
      familyParentPlans: AggregatedBundlePriceDto[],
      defaultPlanId?: string,
    ): string {
      const parentPlanBundlePricesByDescendingPrice = familyParentPlans;

      parentPlanBundlePricesByDescendingPrice.sort(
        (a, b) => (b.Plan?.DataAllowance ?? 0) - (a.Plan?.DataAllowance ?? 0),
      );

      if (
        parentPlanBundlePricesByDescendingPrice.length > 0 &&
        parentPlanBundlePricesByDescendingPrice[0].Plan?.PlanId
      ) {
        const planId = parentPlanBundlePricesByDescendingPrice[0].Plan?.PlanId;

        return `${planId}`;
      } else {
        return defaultPlanId || '';
      }
    },
    _createFamilyPlanToParentPlanMap(plans: Plan[]) {
      const newMap: ParentPlanIdToFamilyPlanIdMap = {};

      for (const plan of plans) {
        if (plan.isFamilyPlan && !newMap[plan.planId]) {
          if (plan.parentPlanIds) {
            for (const parentPlanId of plan.parentPlanIds) {
              const key = `${parentPlanId}_${plan.planName?.toLocaleLowerCase()}`;
              newMap[key] = plan.planId;
            }
          }
        }
      }

      this.parentPlanIdToFamilyPlanIdMap = newMap;
    },
    _createFamilyPlanToParentPlanMapForPDP(plans: AggregatedPlanDto[]) {
      const newMap: ParentPlanIdToFamilyPlanIdMap = {};

      const selectableFamilyPlansAllowances = [30, 100];

      const selectableFamilyPlansByAllowance = plans.filter(
        (plan) =>
          (selectableFamilyPlansAllowances.includes(plan.DataAllowance) ||
            selectableFamilyPlansAllowances.includes(
              plan.RegularDataAllowance,
            )) &&
          plan.IsFamilyPlan === true,
      );

      for (const plan of selectableFamilyPlansByAllowance) {
        if (plan.ParentPlanIds) {
          for (const parentPlanId of plan.ParentPlanIds) {
            const key = `${parentPlanId}_${plan.PlanName?.toLocaleLowerCase()}`;
            if (!newMap[key]) {
              newMap[key] = plan.PlanId;
            }
          }
        }
      }

      this.parentPlanIdToFamilyPlanIdMap = newMap;
    },
    resetExistingCustomerState() {
      this.existingCustomer.isIdentified = false;
      this.existingCustomer.hasPlans = false;
      this.existingCustomer.plans = [];
      this.selectedExistingCustomerPlanNumber = '';
      this.allowedNumberOfConnections = DEFAULT_MAX_ALLOWED_FAMILY_CONNECTIONS;
    },
    resetToNewCustomerFlowOnFamilyPage() {
      this.inputs.bindingPeriodRadioValue = '12';
      this.numberOfFamilyPlanConnections = 1;

      this.resetExistingCustomerState();

      this.ui.showNewCustomerFlow = true;
      this.ui.showPdpDrawerStart = false;
      this.ui.bankIdTestPassthroughIsSelected = false;
      this.ui.generalErrorMessage = undefined;

      this._initGeneral();
    },
    initializeDrawerOnFamilyPage() {
      this.resetExistingCustomerState();
      this._initGeneral();
      this.ui.showNewCustomerFlow = true;
      this.inputs.bindingPeriodRadioValue = '12';
      this.numberOfFamilyPlanConnections = 1;
      this.ui.isProductPage = false;

      if (this.ui.showFamilyDrawer) {
        this.ui.showFamilyDrawer = false;
      }
    },
    initializeDrawerOnPDP() {
      this._initPDP();
      this.numberOfFamilyPlanConnections = 1;
      this.ui.isProductPage = true;
    },
    bankIdTestPassthrough() {
      if (!this.ui.bankIdTestPassthroughIsSelected) {
        this.resetExistingCustomerState();
      }
      this.ui.showNewCustomerFlow = false;
      this.ui.showFamilyDrawer = true;
      this.ui.showPdpDrawerStart = false;
      this.ui.bankIdTestPassthroughIsSelected = true;

      if (this.ui.isProductPage) {
        this._initPDP();
      }
    },
    addExistingCustomer() {
      if (this.ui.bankIdTestPassthroughIsSelected) {
        this.resetExistingCustomerState();
      }
      this.ui.showNewCustomerFlow = false;
      this.ui.showFamilyDrawer = true;
      this.ui.showPdpDrawerStart = false;
      this.ui.bankIdTestPassthroughIsSelected = false;
    },
    addNewCustomer() {
      this.resetExistingCustomerState();
      this.ui.showNewCustomerFlow = true;
      this.ui.showFamilyDrawer = true;
      this.ui.showPdpDrawerStart = false;

      if (this.ui.isProductPage) {
        this._initPDP();
      } else {
        this._initGeneral();
      }
    },
    showFamilyDrawerPdpStart() {
      if (this.ui.isProductPage) {
        this.ui.showNewCustomerFlow = false;
        this.ui.showFamilyDrawer = true;
        this.ui.showPdpDrawerStart = true;

        this.existingCustomer.isIdentified = false;
      } else {
        this.ui.showNewCustomerFlow = false;
        this.ui.showFamilyDrawer = false;
        this.ui.showPdpDrawerStart = false;
        this.existingCustomer.isIdentified = false;
      }
    },
    incrementFamilyConnections(): void {
      if (
        this.numberOfFamilyPlanConnections >= this.allowedNumberOfConnections
      ) {
        this.numberOfFamilyPlanConnections = this.allowedNumberOfConnections;
        return;
      }

      this.numberOfFamilyPlanConnections += 1;
    },
    decrementFamilyConnections(): void {
      if (this.numberOfFamilyPlanConnections <= 1) {
        this.numberOfFamilyPlanConnections = 1;
        return;
      }

      this.numberOfFamilyPlanConnections -= 1;
    },
    async getCustomerPlansEligibleForFamilyConnections() {
      const { $api } = useNuxtApp();

      const result =
        await $api.CustomerModule.getCustomerPlansEligibleForFamilyConnections();
      this.existingCustomer.plans = result.CustomerPlans;
      this.existingCustomer.isIdentified = true;

      if (this.existingCustomer.plans.length > 0) {
        this.existingCustomer.hasPlans = true;
        this.selectedExistingCustomerPlanNumber =
          this.existingCustomer.plans[0].Number;
        this.allowedNumberOfConnections =
          this.existingCustomer.plans[0].AllowedFamilyConnections;
      } else {
        this.existingCustomer.hasPlans = false;
      }

      if (this.ui.isProductPage) {
        this._initPDP();
      }
    },
  },
  getters: {
    selectedRegularPlanId(state: State): string {
      // Existing customer main plan
      if (state.existingCustomer.plans.length > 0) {
        const currentSelectedPlan = state.existingCustomer.plans.find(
          (p) => p.Number === state.selectedExistingCustomerPlanNumber,
        );
        return currentSelectedPlan ? currentSelectedPlan.PlanId.toString() : '';
      }

      // New customer main plan
      return state.selectedNewCustomerPlanId;
    },
    selectedFamilyPlan(state: State): Plan | null {
      const planStore = usePlanStore();
      const productPageStore = useMyProductPageStore();

      const parentPlanId = +this.selectedRegularPlanId;
      if (!parentPlanId) {
        return null;
      }

      let familyPlanId: number | undefined;

      if (state.existingCustomer.plans.length > 0) {
        familyPlanId =
          state.existingCustomer.plans.find(
            (plan) => plan.Number === state.selectedExistingCustomerPlanNumber,
          )?.AllowedFamilyPlanId ?? undefined;
      } else {
        const parentPlanName =
          this.selectedParentPlan?.planName?.toLocaleLowerCase() || '';
        familyPlanId =
          state.parentPlanIdToFamilyPlanIdMap[
            `${parentPlanId}_${parentPlanName}`
          ];
      }

      if (!familyPlanId) {
        return null;
      }

      if (state.ui.isProductPage) {
        const familyPlanBundlePricesFromProductVariant =
          (productPageStore.currentVariant?.Prices.BundlePrices || []).filter(
            (bp) =>
              bp.Plan?.PlanId === familyPlanId &&
              bp.Plan?.IsStudent === false &&
              bp.Plan.IsFamilyPlan === true,
          ) ?? null;
        const selectedFamilyPlan = mapProductDataBundlePricesToPlanObject(
          familyPlanBundlePricesFromProductVariant,
        );
        return selectedFamilyPlan;
      } else {
        return (
          planStore.familyPlans.find((plan) => plan.planId === familyPlanId) ??
          null
        );
      }
    },
    selectedFamilyPlanBundlePrice(): AggregatedBundlePriceDto | null {
      if (!this.selectedFamilyPlan) {
        return null;
      }

      return (
        this.selectedFamilyPlan.bundlePrices.find(
          findBundlePriceByBindingPeriod(this.selectedBindingPeriod),
        ) ?? null
      );
    },
    selectedParentPlan(state: State): Plan | null {
      const planStore = usePlanStore();
      const productPageStore = useMyProductPageStore();
      const selectedRegularPlanId = parseInt(this.selectedRegularPlanId);

      let parentPlan = null;

      if (state.ui.isProductPage) {
        const productVariantParentPlan = (
          productPageStore.currentVariant?.Prices?.BundlePrices || []
        ).filter(
          (bp) =>
            bp.Plan?.PlanId === selectedRegularPlanId &&
            bp.Plan?.IsFamilyAllowed === true &&
            bp.Plan?.IsStudent === false,
        );

        parentPlan = mapProductDataBundlePricesToPlanObject(
          productVariantParentPlan,
        );
      } else {
        parentPlan = planStore.familyParentPlans?.find(
          (f) => f.planId === selectedRegularPlanId,
        );
      }

      if (!parentPlan) {
        parentPlan = planStore.familyParentPlans?.find(
          (f) => f.planId === this.compatibilityParentPlanId,
        );
      }

      return parentPlan ?? null;
    },
    selectedParentPlanBundlePrice(): AggregatedBundlePriceDto | null {
      if (!this.selectedParentPlan) {
        return null;
      }

      return (
        this.selectedParentPlan.bundlePrices.find(
          findBundlePriceByBindingPeriod(this.selectedBindingPeriod),
        ) ?? null
      );
    },
    selectedBindingPeriod(state: State): AllowedFamilyPlanBindingPeriodMonths {
      if (!state.ui.isProductPage) {
        return bindingPeriodOrDefault(+state.inputs.bindingPeriodRadioValue);
      }

      const productPageStore = useMyProductPageStore();
      const bindingPeriodBasedOnPartPayment =
        productPageStore.selectedBindingPeriod;
      return bindingPeriodOrDefault(bindingPeriodBasedOnPartPayment);
    },
    familyPlanBundlePricesToDisplay(state: State): AggregatedBundlePriceDto[] {
      const planStore = usePlanStore();
      const productPageStore = useMyProductPageStore();
      const bundlePrices: AggregatedBundlePriceDto[] = [];

      if (state.ui.isProductPage) {
        productPageStore.currentFamilyParentPlansForDrawer.forEach((bp) => {
          if (bp.BindingPeriod.Months === this.selectedBindingPeriod) {
            bundlePrices.push(bp);
          }
        });
      } else {
        planStore.familyParentPlans.forEach((plan) => {
          const bundlePrice = plan.bundlePrices.find(
            (bundlePrice) =>
              bundlePrice.BindingPeriod.Months === this.selectedBindingPeriod,
          );

          if (bundlePrice && bundlePrice.Plan) {
            bundlePrices.push(bundlePrice);
          }
        });
      }

      return bundlePrices.sort((b1, b2) => {
        const price1 = b1.Plan?.DataAllowance ?? 1;
        const price2 = b2.Plan?.DataAllowance ?? 1;

        if (price1 < price2) {
          return 1;
        } else if (price1 > price2) {
          return -1;
        } else {
          return 0;
        }
      });
    },
    requestPayloadForAddToCart(state: State): AddBundleToCartRequestModel {
      if (state.ui.showNewCustomerFlow) {
        return {
          BundleRequestType: AddBundleRequestType.FAMILY_PLAN_NEW_CUSTOMER,
          BundlePriceId: this.selectedParentPlanBundlePrice?.BundlePriceId,
          NumberOfFamilyPlans: state.numberOfFamilyPlanConnections,
          MainFamilyPlanCellPhoneNumber: undefined,
          FamilyBundlePriceId:
            this.selectedFamilyPlanBundlePrice?.BundlePriceId,
        };
      }

      return {
        BundlePriceId: this.selectedFamilyPlanBundlePrice?.BundlePriceId,
        BundleRequestType: AddBundleRequestType.FAMILY_PLAN_EXISTING_CUSTOMER,
        NumberOfFamilyPlans: state.numberOfFamilyPlanConnections,
        MainFamilyPlanCellPhoneNumber: state.selectedExistingCustomerPlanNumber,
        FamilyBundlePriceId: this.selectedFamilyPlanBundlePrice?.BundlePriceId,
      };
    },
    shouldIncludeParentPlanInPricing(state: State): boolean {
      return state.ui.showNewCustomerFlow;
    },
    priceOfAddedFamilyConnections(state: State): number {
      const familyConnectionPrice =
        this.selectedFamilyPlan?.bundlePrices.find(
          findBundlePriceByBindingPeriod(this.selectedBindingPeriod),
        )?.Plan?.MonthlyFee ?? 0;

      return state.numberOfFamilyPlanConnections * familyConnectionPrice;
    },
    regularPriceOfAddedFamilyConnections(state: State): number {
      const familyConnectionPrice =
        this.selectedFamilyPlan?.bundlePrices.find(
          findBundlePriceByBindingPeriod(this.selectedBindingPeriod),
        )?.Plan?.RegularMonthlyFee ?? 0;

      return state.numberOfFamilyPlanConnections * familyConnectionPrice;
    },
    existingCustomerSelectedPlanAllowedConnections(
      state: State,
    ): number | null {
      if (
        !state.existingCustomer.hasPlans ||
        state.existingCustomer.plans.length === 0
      ) {
        return null;
      }

      return (
        state.existingCustomer.plans.find(
          (plan) => plan.Number === state.selectedExistingCustomerPlanNumber,
        )?.AllowedFamilyConnections ?? null
      );
    },
    summary_totalMontlyCost(state: State): number | null {
      let totalPrice = 0;

      if (this.shouldIncludeParentPlanInPricing) {
        const parentPlanMonthlyPrice =
          this.selectedParentPlan?.bundlePrices.find(
            findBundlePriceByBindingPeriod(this.selectedBindingPeriod),
          )?.Plan?.MonthlyFee ?? 0;

        totalPrice += parentPlanMonthlyPrice;
      }

      totalPrice += this.priceOfAddedFamilyConnections;

      if (totalPrice === 0) {
        return null;
      }

      return totalPrice;
    },
    summary_plansToPurchaseText(state: State): string {
      let plansToPurchaseText = '';

      const parentPlanBundlePrice = this.selectedParentPlan?.bundlePrices.find(
        findBundlePriceByBindingPeriod(this.selectedBindingPeriod),
      );

      const familyConnectionBundlePrice =
        this.selectedFamilyPlan?.bundlePrices.find(
          findBundlePriceByBindingPeriod(this.selectedBindingPeriod),
        );

      if (
        this.shouldIncludeParentPlanInPricing &&
        this.selectedParentPlan?.productName &&
        parentPlanBundlePrice?.Plan?.MonthlyFee
      ) {
        plansToPurchaseText +=
          this.selectedParentPlan?.productName +
          ' för ' +
          parentPlanBundlePrice?.Plan?.MonthlyFee +
          ' kr/mån';
      }

      for (let i = 0; i < state.numberOfFamilyPlanConnections; i++) {
        if (
          this.selectedFamilyPlan?.productName &&
          familyConnectionBundlePrice?.Plan?.MonthlyFee
        ) {
          if (plansToPurchaseText.length > 0) {
            plansToPurchaseText += ' + ';
          }
          plansToPurchaseText +=
            this.selectedFamilyPlan?.productName +
            ' för ' +
            familyConnectionBundlePrice?.Plan?.MonthlyFee +
            ' kr/mån';
          if (this.selectedBindingPeriod !== 0) {
            plansToPurchaseText += ' i ' + this.selectedBindingPeriod + ' mån';
          }
        }
      }

      return plansToPurchaseText;
    },
    trackingData(): ProductPageTrackingData {
      const createItem = (
        plan: Plan | null,
        bundlePrice: AggregatedBundlePriceDto | null,
      ) => ({
        item_brand: 'Halebop',
        item_category: 'Abonnemang',
        item_id: plan?.planId,
        item_name: plan?.productName,
        item_variant: `${this.selectedBindingPeriod} månader`,
        price: bundlePrice?.Plan?.MonthlyFee,
        quantity: 1,
      });

      const items: any[] = [];

      if (this.shouldIncludeParentPlanInPricing) {
        items.push(
          createItem(
            this.selectedParentPlan,
            this.selectedParentPlanBundlePrice,
          ),
        );
      }

      items.push(
        createItem(this.selectedFamilyPlan, this.selectedFamilyPlanBundlePrice),
      );

      if (this.requestPayloadForAddToCart.NumberOfFamilyPlans === 2) {
        items.push(
          createItem(
            this.selectedFamilyPlan,
            this.selectedFamilyPlanBundlePrice,
          ),
        );
      }

      return {
        productPriceId: 0,
        downPayTime: '0',
        ecommerce: {
          items,
        },
      };
    },
    getDefaultFamilyConnectionPlan(): Plan | null {
      const planStore = usePlanStore();
      const plans = planStore.familyPlans;
      const defaultPlan = plans.find((plan) => plan.planName.includes('30GB'));
      return defaultPlan ?? null;
    },
    getSelectedFamilyConnectionPlan(): Plan | null {
      const planStore = usePlanStore();
      const plans = planStore.familyPlans;
      const defaultPlan = plans.find((plan) =>
        plan.planName.includes(this.selectedParentPlan?.planName ?? '30GB'),
      );
      return defaultPlan ?? null;
    },
    FamilyPlanHasDiscount(): boolean {
      const planStore = usePlanStore();
      const plans = planStore.familyPlans;
      const discounts = plans
        .flatMap((plan) => plan.bundlePrices)
        .flatMap((bundlePrice) => bundlePrice.Discounts);

      return discounts.length > 0 ? true : false;
    },
    compatibilityParentPlanId(): number {
      // Om föräldrarplanen inte hittades och familjeplanen har en alterantiv förälderplan,
      // försök då att hitta den alternativa föräldrarplanen.
      // T.ex. 3F har H4 och IE som föräldrar.
      // Om IE är föräldrarplan (ej aktiv), använd H4 som stand-in istället.
      // Informationen används ändå inte i någon större utstreckning eftersom det bara inträffar vid
      // befintliga nummer där abonnemanget inte är aktivt (IE, HE)
      let compatibilityParentPlanId: number = 0;

      switch (+this.selectedRegularPlanId) {
        case 23: // ID - 50GB
          compatibilityParentPlanId = 11;
          break;
        case 24: // LD - 20GB
          compatibilityParentPlanId = 12;
          break;
        case 26: // HE - 30GB
          compatibilityParentPlanId = 12;
          break;
        case 27: // IE - 100GB
          compatibilityParentPlanId = 11;
          break;
      }

      return compatibilityParentPlanId;
    },
  },
});
